import { useEffect, useState, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Module-level default so the reference is stable and doesn't retrigger effects.
const DEFAULT_UNWRAP = (d) => d?.items || [];

/**
 * Fetch a list resource that auto-refreshes when:
 *   - the component mounts
 *   - the tab regains visibility (user switches back)
 *   - the window regains focus
 *   - the "hp:products-changed" custom event fires (dispatched from admin CRUD)
 *   - a cross-tab "storage" event fires for hp:products-changed
 */
export default function useAutoRefreshList(path, unwrap) {
  const [items, setItems] = useState([]);
  const effectiveUnwrap = unwrap || DEFAULT_UNWRAP;

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}${path}`, {
        headers: { "Cache-Control": "no-cache" },
        params: { _: Date.now() },
      });
      setItems(effectiveUnwrap(data));
    } catch {}
  }, [path, effectiveUnwrap]);

  useEffect(() => {
    load();
    const onVisible = () => { if (document.visibilityState === "visible") load(); };
    const onFocus = () => load();
    const onProductsChanged = () => load();
    const onStorage = (e) => { if (e.key === "hp:products-changed") load(); };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onFocus);
    window.addEventListener("hp:products-changed", onProductsChanged);
    window.addEventListener("storage", onStorage);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("hp:products-changed", onProductsChanged);
      window.removeEventListener("storage", onStorage);
    };
  }, [load]);

  return { items, reload: load };
}
